import { base64ToCursor } from './sequence-id.js';

export type ScopedSnapshotSequence =
  | { readonly kind: 'unhandled' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'cursor'; readonly cursor: Uint8Array };

const INVALID_SEQUENCE_FIELD = Symbol('invalid-sequence-field');
type SequenceField = string | null | undefined | typeof INVALID_SEQUENCE_FIELD;

/** Resolve each event from the source market's chain cursor. */
export function scopedSnapshotSequence(
  snapshot: unknown,
  subject: string,
  _event: unknown,
): ScopedSnapshotSequence {
  const tokens = subject.split('.');
  if (tokens[0] !== 'morfi') return { kind: 'unhandled' };
  if (tokens[1] === 'market') {
    const scoped = marketSequence(snapshot, tokens);
    if (scoped.kind !== 'unhandled') return scoped;
  }
  if (tokens[1] === 'amm') {
    const scoped = ammSequence(snapshot, tokens);
    if (scoped.kind !== 'unhandled') return scoped;
  }
  if (isRecord(snapshot) && Object.hasOwn(snapshot, 'snapshotSequence')) {
    return oneSequence(field(snapshot, 'snapshotSequence'));
  }
  return { kind: 'unhandled' };
}

function marketSequence(snapshot: unknown, tokens: readonly string[]): ScopedSnapshotSequence {
  const market = tokens[2];
  if (market === undefined || market === '*' || market === 'created') {
    return { kind: 'unhandled' };
  }
  const row = rows(snapshot).find((candidate) => candidate.pubkey === market);
  if (row === undefined) return { kind: 'unhandled' };

  if (tokens.includes('trade')) return oneSequence(field(row, 'marketStateSequence'));
  if (
    tokens.includes('boost') ||
    tokens.includes('backing') ||
    tokens.includes('lifecycle') ||
    tokens.includes('resolution')
  ) {
    return oneSequence(field(row, 'marketStateSequence'));
  }
  return { kind: 'unhandled' };
}

function ammSequence(snapshot: unknown, tokens: readonly string[]): ScopedSnapshotSequence {
  const mint = tokens[2];
  if (
    mint === undefined ||
    mint === '*' ||
    tokens[3] !== 'user' ||
    tokens[4] === undefined ||
    tokens[5] !== 'activity'
  ) {
    return { kind: 'unhandled' };
  }
  for (const row of rows(snapshot)) {
    if (row.mint === mint) return oneSequence(field(row, 'lastSeq'));
    const outcome = outcomes(row).find((candidate) => candidate.mint === mint);
    if (outcome !== undefined) return oneSequence(field(outcome, 'outcomeStateSequence'));
  }
  return { kind: 'unhandled' };
}

function rows(value: unknown): ReadonlyArray<Record<string, unknown>> {
  if (!isRecord(value)) return [];
  const data = value.data;
  if (Array.isArray(data)) return data.filter(isRecord);
  return [value];
}

function outcomes(row: Readonly<Record<string, unknown>>): ReadonlyArray<Record<string, unknown>> {
  const value = row.outcomes;
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function field(row: Readonly<Record<string, unknown>>, name: string): SequenceField {
  if (!Object.hasOwn(row, name)) return undefined;
  const value = row[name];
  return value === null || typeof value === 'string' ? value : INVALID_SEQUENCE_FIELD;
}

function oneSequence(value: SequenceField): ScopedSnapshotSequence {
  if (value === undefined) return { kind: 'unhandled' };
  if (value === INVALID_SEQUENCE_FIELD) return { kind: 'invalid' };
  if (value === null) return { kind: 'empty' };
  const cursor = base64ToCursor(value);
  return cursor === null ? { kind: 'invalid' } : { kind: 'cursor', cursor };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
